'use client';

import React, { type Dispatch, type SetStateAction, useMemo } from 'react';
import type { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { DefaultValuesCSW } from '@/app/common/CSW/services/types';
import type {
  HandleDynamicFiltersResponseChanged,
  HandleHotelChosenData,
  HandleNewSearch,
  HandleSearchChanged,
} from '@/app/common/PageWrapperClientTypes.tsx';
import type { StarTrack } from '@/app/common/starTrack/startrack.ts';
import type { ListViewType } from '@/app/common/viewModeUtils.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { SubSourceType } from './starTrack/events/searchRequested';
import type { PeopleData } from '../components/search/utils';

export type HandleHotelChosenOptions = {
  refreshSearch?: boolean;
  subSourceType?: SubSourceType;
  scrollToTop?: boolean;
  dealCard?: DealCard;
  openInNewTab?: boolean;
  generateClickId?: boolean;
};

export type BackToResultsOptions = {
  // Defaults to true. Set to false when the hotel we came from is not bookable (hotel not
  // available, rooms sold out): pinning it first with the "you were interested in" band would send
  // the user straight back into the same dead end. (MPH-784)
  highlightHotel?: boolean;
};

export type CommonClient = {
  // The results page reports the view mode it is actually rendering, so RESULT_SHOWN follows the map
  // toggle instead of a value resolved before it. (MPH-931)
  reportListView: (listView: ListViewType) => void;
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isEvoLayout: boolean;
  isEvoResultsLayout: boolean;
  starTrack: StarTrack;
  handleSearchChanged: HandleSearchChanged;
  handleNewSearch: HandleNewSearch;
  handlePagination: (searchParams: SearchParams) => void;
  handleBackToResults: (options?: BackToResultsOptions) => void;
  handleChangeTransport: () => void;
  handleChangeRoom: (koProviderId?: string) => void;
  handleChangeStay: (shouldAddBrowserHistory?: boolean, options?: BackToResultsOptions) => void;
  handleEvoGoToCheckout: (props: {
    roomOfferId: string;
    mealPlanId: string;
    mealPlanCode: string;
    transportId?: string;
    brandedFareId?: string;
  }) => void;
  handleEvoGoToChangeTransport: (props: { roomOfferId: string; mealPlanId: string; mealPlanCode: string }) => void;
  handleEvoDefaultTransportShown: (transportId: string) => void;
  handleEvoGoToHotelDetails: (mealPlan?: { mealPlanId: string; mealPlanCode: string }) => void;
  handleTransportChosen: (transportId: string, brandedFareId?: string) => void;
  handleFlightClicked: (flightClickId: string, elegibleBrandedFares: boolean) => void;
  handleHotelChosen: (hotelIdChosen: string, options?: HandleHotelChosenOptions) => HandleHotelChosenData;
  handleRoomChosen: (roomOfferId: string, mealPlanId: string, mealPlanCode: string, defaultTransportId: string) => void;
  handleSearchAPIStatusChanged: (newApiStatus: APIStatusEnum) => void;
  handleSearchProductsDataChanged: (newProductsData: ProductsData) => void;
  handleRefreshSearchFromHotelDetails: (params: {
    subSourceType?: SubSourceType;
    syncCSWDates?: { dateFrom: string; dateTo: string };
    syncCSWTravellers?: PeopleData;
    extraQueryParamsToSet?: { [key: string]: string }[];
    extraQueryParamsToRemove?: string[];
  }) => Promise<string>;
  handleDynamicFiltersResponseChanged: HandleDynamicFiltersResponseChanged;
  setDefaultValuesCSW: Dispatch<SetStateAction<DefaultValuesCSW>>;
  externalHeaderEnabled: boolean;
};
const CommonClientContext = React.createContext<CommonClient>({} as CommonClient);

export default function CommonClientProvider({
  commonClient,
  children,
}: {
  commonClient: CommonClient;
  children: React.ReactNode;
}) {
  const contextValue = useMemo(() => commonClient, [commonClient]);
  return <CommonClientContext.Provider value={contextValue}>{children}</CommonClientContext.Provider>;
}

export const useCommonClient = () => React.useContext(CommonClientContext);
